import { useState, useCallback, Fragment } from "react";
import MinimalLexical from "./LexicalText/MinimalLexical.jsx";
import ConfirmModal from "../../ConfirmModal.jsx";
import { isProd } from "../../../devprodConfig";

const KL_OPTIONS = ["-", "KL1", "KL2", "KL3", "KL4", "KL5"];

const KL_COLORS = {
  KL1: "#ba3b46",
  KL2: "#ba3b46",
  KL3: "#d0c407",
  KL4: "#04aa00",
  KL5: "#04aa00",
  "-": "#888",
};

const newRow = () => ({
  id: crypto.randomUUID(),
  kunto: "KL3",
  label: "",
  description: { lexicalId: crypto.randomUUID(), value: "", html: "" },
});

export default function KuntoTable({ data, onChange }) {
  const [headerLabel, setHeaderLabel] = useState(data?.headerLabel || "");
  const [headerDescription, setHeaderDescription] = useState(data?.headerDescription || "");
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [rows, setRows] = useState(() =>
    data?.rows?.length
      ? data.rows.map(r => ({
          ...r,
          description: r.description && typeof r.description === "object"
            ? r.description
            : { lexicalId: crypto.randomUUID(), value: r.description || "", html: "" },
        }))
      : [newRow()]
  );

  const commit = useCallback((nextRows, nextLabel, nextDesc) => {
    onChange?.({
      ...data,
      headerLabel: nextLabel ?? headerLabel,
      headerDescription: nextDesc ?? headerDescription,
      rows: nextRows ?? rows,
    });
  }, [data, onChange, headerLabel, headerDescription, rows]);

  const handleBlur = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      commit(rows, headerLabel, headerDescription);
    }
  };

  const updateRow = (id, field, value) => {
    const next = rows.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, [field]: value };
      if (field === "kunto" && value === "-") updated.description = { lexicalId: crypto.randomUUID(), value: "Ei ole.", html: "<p>Ei ole.</p>" };
      return updated;
    });
    setRows(next);
    if (field === "kunto") commit(next, null, null);
  };

  const updateDescription = (id, descData) => {
    const next = rows.map(r => r.id === id ? { ...r, description: descData } : r);
    setRows(next);
    commit(next, null, null);
  };

  const addRow = () => {
    const next = [...rows, newRow()];
    setRows(next);
    commit(next, null, null);
  };

  const removeRow = (id) => {
    const next = rows.filter(r => r.id !== id);
    setRows(next);
    commit(next, null, null);
    setConfirmDeleteId(null);
  };

  const DEV_SAMPLES = [
    { kunto: 'KL2', label: 'Pinnoite:', desc: 'Pinnoitteessa havaittiin merkittäviä halkeamia ja irtoamista. Uusiminen tarpeen lähivuosina.' },
    { kunto: 'KL3', label: 'Rakenne:', desc: 'Rakenteessa näkyy kosteusrasituksen jälkiä. Tarkempi tutkimus suositellaan.' },
    { kunto: 'KL4', label: 'Tiivisteet:', desc: 'Tiivisteet ovat hyvässä kunnossa, normaalia kulumista havaittavissa.' },
    { kunto: 'KL2', label: 'Pellitykset:', desc: 'Pellityksissä on ruostumista ja osittainen uusiminen tarvitaan.' },
    { kunto: 'KL3', label: 'Liikuntasaumat:', desc: 'Liikuntasaumat toimivat, mutta ikääntymistä on havaittavissa.' },
    { kunto: 'KL1', label: 'Vedenpoisto:', desc: 'Vedenpoistossa on vakavia puutteita. Välitön korjaus tarpeen.' },
    { kunto: 'KL4', label: 'Ikkunat:', desc: 'Ikkunat ovat toimintakunnossa. Säännöllinen huolto riittää.' },
  ];
  const devFillData = () => {
    const pick = arr => arr[Math.floor(Math.random() * arr.length)];
    const next = rows.map(r => {
      const desc = pick(DEV_SAMPLES).desc;
      return { ...r, description: { lexicalId: crypto.randomUUID(), value: desc, html: `<p>${desc}</p>` } };
    });
    setRows(next);
    commit(next, null, null);
  };

  return (
    <>
      <style>{`
        .kunto-header {
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: nowrap;
        }
        .kunto-row {
          display: grid;
          grid-template-columns: 80px 1fr auto;
          grid-template-rows: auto auto;
          gap: 6px;
          margin-top: 8px;
        }
        .kunto-row-kl    { grid-column: 1; grid-row: 1; align-self: start; }
        .kunto-row-label { grid-column: 2; grid-row: 1; align-self: start; }
        .kunto-row-del   { grid-column: 3; grid-row: 1; align-self: start; }
        .kunto-row-desc  { grid-column: 1 / -1; grid-row: 2; }

        @media (min-width: 576px) {
          .kunto-row {
            grid-template-columns: 80px minmax(140px, 200px) 1fr auto;
            grid-template-rows: auto;
          }
          .kunto-row-kl    { grid-column: 1; grid-row: 1; align-self: start; }
          .kunto-row-label { grid-column: 2; grid-row: 1; align-self: start; }
          .kunto-row-desc  { grid-column: 3; grid-row: 1; }
          .kunto-row-del   { grid-column: 4; grid-row: 1; align-self: start; }
        }

        .kunto-header-label { display: none; }
        @media (min-width: 576px) {
          .kunto-header-label { display: block; }
        }
      `}</style>

      <div className="p-0 border rounded bg-white" onBlur={handleBlur}>
        {/* Header */}
        <div className="kunto-header px-2 pt-2 pb-1 border-bottom border-success border-2">
          <span className="fw-bold text-secondary kunto-header-label" style={{ fontSize: "0.8rem", width: 80, flexShrink: 0, textAlign: "center" }}>
            Kunto
          </span>
          <input
            className="form-control form-control-sm fw-bold"
            style={{ flex: "1 1 140px", minWidth: 0 }}
            value={headerLabel}
            onChange={e => setHeaderLabel(e.target.value)}
            placeholder="Otsikko (esim. Asuntojen ikkunat:)"
          />
          <input
            className="form-control form-control-sm"
            style={{ flex: "2 1 160px", minWidth: 0 }}
            value={headerDescription}
            onChange={e => setHeaderDescription(e.target.value)}
            placeholder="Kuvaus"
          />
        </div>

        {/* Rows */}
        <div className="px-2 pb-2">
          {rows.map((row) => (
            <div key={row.id} className="kunto-row">
              {/* KL dropdown */}
              <div className="kunto-row-kl">
                <select
                  className="form-select form-select-sm fw-bold"
                  style={{ width: "100%", color: KL_COLORS[row.kunto] || "#333" }}
                  value={row.kunto}
                  onChange={e => updateRow(row.id, "kunto", e.target.value)}
                >
                  {KL_OPTIONS.map(kl => (
                    <option key={kl} value={kl} style={{ color: KL_COLORS[kl] }}>{kl}</option>
                  ))}
                </select>
              </div>

              {/* Label */}
              <div className="kunto-row-label">
                <input
                  className="form-control form-control-sm fw-bold w-100"
                  value={row.label}
                  onChange={e => updateRow(row.id, "label", e.target.value)}
                  placeholder="Kohde (esim. Ulkopuite:)"
                />
              </div>

              {/* Description */}
              <div className="kunto-row-desc">
                <MinimalLexical
                  data={row.description}
                  onChange={descData => updateDescription(row.id, descData)}
                  placeholder="Kuvaus"
                />
              </div>

              {/* Remove */}
              <div className="kunto-row-del">
                <button
                  className="btn btn-outline-danger btn-sm"
                  style={{ padding: "2px 7px" }}
                  onClick={() => setConfirmDeleteId(row.id)}
                  disabled={rows.length === 1}
                >✕</button>
              </div>
            </div>
          ))}

          <div className="d-flex justify-content-center align-items-center gap-2 mt-2">
            <button className="btn btn-outline-primary btn-sm" onClick={addRow}>
              + Lisää rivi
            </button>
          </div>
        </div>
      </div>
      <ConfirmModal
        show={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => removeRow(confirmDeleteId)}
        title="Poista rivi"
        message="Haluatko varmasti poistaa tämän rivin?"
        confirmText="Poista"
        cancelText="Peru"
      />
    </>
  );
}
